import { useEffect, useRef } from 'react'
import { Bell, X, CheckCheck, Package, Trash2, CheckCircle, Truck, Star, XCircle, ShoppingBag } from 'lucide-react'
import { useNotifications } from '../contexts/NotificationContext'
import { useSettings } from '../contexts/SettingsContext'
import './NotificationPanel.css'

const TYPE_ICON = {
  order_created:   ShoppingBag,
  order_confirmed: CheckCircle,
  order_processing:Package,
  order_shipped:   Truck,
  order_delivered: Star,
  order_cancelled: XCircle,
}

const TYPE_COLOR = {
  order_created:   '#3b82f6',
  order_confirmed: '#059669',
  order_processing:'#8b5cf6',
  order_shipped:   '#0ea5e9',
  order_delivered: '#059669',
  order_cancelled: '#ef4444',
}

function timeAgo(dateStr, lang) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 60)  return lang === 'fr' ? "à l'instant" : 'just now'
  if (diff < 3600) {
    const m = Math.floor(diff / 60)
    return lang === 'fr' ? `il y a ${m} min` : `${m}m ago`
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600)
    return lang === 'fr' ? `il y a ${h}h` : `${h}h ago`
  }
  const d = Math.floor(diff / 86400)
  return lang === 'fr' ? `il y a ${d}j` : `${d}d ago`
}

export default function NotificationPanel({ open, onClose }) {
  const { notifications, unread, markRead, markAllRead, remove } = useNotifications()
  const { lang } = useSettings()
  const panelRef = useRef()

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onClose])

  // Lock scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  return (
    <>
      {/* Backdrop */}
      <div className={`np-backdrop ${open ? 'np-backdrop-visible' : ''}`} onClick={onClose} />

      {/* Panel */}
      <div ref={panelRef} className={`np-panel ${open ? 'np-panel-open' : ''}`} role="dialog" aria-label="Notifications">
        {/* Header */}
        <div className="np-head">
          <div className="np-head-left">
            <Bell size={18} />
            <span>{lang === 'fr' ? 'Notifications' : 'Notifications'}</span>
            {unread > 0 && <span className="np-unread-badge">{unread}</span>}
          </div>
          <div className="np-head-right">
            {unread > 0 && (
              <button className="np-mark-all" onClick={markAllRead} title={lang === 'fr' ? 'Tout marquer comme lu' : 'Mark all read'}>
                <CheckCheck size={15} />
              </button>
            )}
            <button className="np-close" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {/* Body */}
        <div className="np-body">
          {notifications.length === 0 ? (
            <div className="np-empty">
              <Bell size={36} strokeWidth={1.2} />
              <p>{lang === 'fr' ? 'Aucune notification' : 'No notifications'}</p>
            </div>
          ) : (
            <div className="np-list">
              {notifications.map(n => {
                const Icon  = TYPE_ICON[n.type] ?? Bell
                const color = TYPE_COLOR[n.type] ?? '#003DA5'
                const isUnread = !n.read_at
                return (
                  <div
                    key={n.id}
                    className={`np-item ${isUnread ? 'np-item-unread' : ''}`}
                    onClick={() => isUnread && markRead(n.id)}
                  >
                    <div className="np-item-icon" style={{ background: `${color}18`, color }}>
                      <Icon size={16} />
                    </div>
                    <div className="np-item-body">
                      <p className="np-item-title">{n.title}</p>
                      <p className="np-item-msg">{n.message}</p>
                      <span className="np-item-time">{timeAgo(n.created_at, lang)}</span>
                    </div>
                    {isUnread && <div className="np-item-dot" />}
                    <button
                      className="np-item-del"
                      onClick={e => { e.stopPropagation(); remove(n.id) }}
                      title={lang === 'fr' ? 'Supprimer' : 'Remove'}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
